import styles from "./node-content-renderer.module.scss";
import FontAwesome from "../../../utilities/FontAwesome";
import classNames from "classnames";
import { IconButton } from "@radix-ui/themes";

// @nosferatu500/react-sortable-tree's package.json "exports" only allows importing "."
// so its node-renderer-default.d.ts NodeRendererProps type isn't importable here.
interface NodeRendererProps {
  node: any;
  path: (string | number)[];
  treeIndex: number;
  isSearchMatch?: boolean;
  isSearchFocus?: boolean;
  canDrag?: boolean;
  scaffoldBlockPxWidth: number;
  toggleChildrenVisibility?: (data: {
    node: any;
    path: (string | number)[];
    treeIndex: number;
  }) => void;
  buttons?: React.ReactNode[];
  className?: string;
  style?: React.CSSProperties;
  title?: any;
  icons?: React.ReactNode[];
  lowerSiblingCounts: number[];
  swapDepth?: number | null;
  swapFrom?: number | null;
  swapLength?: number | null;
  listIndex: number;
  treeId: string;
  rowDirection?: string;
  connectDragPreview: (node: React.ReactElement, opts?: any) => any;
  connectDragSource: (node: React.ReactElement, opts?: any) => any;
  parentNode?: any;
  isDragging: boolean;
  didDrop: boolean;
  draggedNode?: any;
  isOver: boolean;
  canDrop?: boolean;
  [key: string]: any;
}

function isDescendant(older: any, younger: any) {
  return (
    !!older.children &&
    typeof older.children !== "function" &&
    older.children.some(
      (child: any) => child === younger || isDescendant(child, younger),
    )
  );
}

const FileThemeNodeContentRenderer = (props: NodeRendererProps) => {
  const {
    scaffoldBlockPxWidth,
    toggleChildrenVisibility = null,
    connectDragPreview,
    connectDragSource,
    isDragging,
    canDrop = false,
    canDrag = false,
    node,
    title = null,
    draggedNode = null,
    path,
    treeIndex,
    isSearchMatch = false,
    isSearchFocus = false,
    icons = [],
    buttons = [],
    className = "",
    style = {},
    didDrop,
    lowerSiblingCounts,
    listIndex,
    swapFrom = null,
    swapLength = null,
    swapDepth = null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    treeId, // Not needed, but preserved for other renderers
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isOver, // Not needed, but preserved for other renderers
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    parentNode = null, // Needed for dndManager
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rowDirection,
    ...otherProps
  } = props;

  const nodeTitle = title || node.title;

  const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
  const isLandingPadActive = !didDrop && isDragging;

  // Construct the scaffold representing the structure of the tree
  const scaffold: React.ReactNode[] = [];
  lowerSiblingCounts.forEach((lowerSiblingCount, i) => {
    scaffold.push(
      <div
        key={`pre_${1 + i}`}
        style={{ width: scaffoldBlockPxWidth }}
        className={styles.lineBlock}
      />,
    );

    if (treeIndex !== listIndex && i === swapDepth) {
      // This row has been shifted, and is at the depth of
      // the line pointing to the new destination
      let highlightLineClass = "";

      if (listIndex === swapFrom! + swapLength! - 1) {
        // This block is on the bottom (target) line
        // This block points at the target block (where the row will go when released)
        highlightLineClass = styles.highlightBottomLeftCorner;
      } else if (treeIndex === swapFrom) {
        // This block is on the top (source) line
        highlightLineClass = styles.highlightTopLeftCorner;
      } else {
        // This block is between the bottom and top
        highlightLineClass = styles.highlightLineVertical;
      }

      scaffold.push(
        <div
          key={`highlight_${1 + i}`}
          style={{
            width: scaffoldBlockPxWidth,
            left: scaffoldBlockPxWidth * i,
          }}
          className={`${styles.absoluteLineBlock} ${highlightLineClass}`}
        />,
      );
    }
  });

  let handle;
  if (canDrag) {
    if (typeof node.children === "function" && node.expanded) {
      // Show a loading symbol on the handle when the children are expanded
      //  and yet still defined by a function (a callback to fetch the children)
      handle = (
        <div className={styles.loadingHandle}>
          <div className={styles.loadingCircle}>
            {[...new Array(12)].map((_, index) => (
              <div
                key={index}
                // className={classnames(
                //     'rst__loadingCirclePoint',
                //     rowDirectionClass
                // )}
              />
            ))}
          </div>
        </div>
      );
    } else {
      // Show the handle used to initiate a drag-and-drop
      handle = connectDragSource(
        <div className={styles.moveHandle}>
          <FontAwesome name={"fa-grip-vertical"} prefix={"fas"} />
        </div>,
        {
          dropEffect: "move",
        },
      );
    }
  }

  const rowClassName = classNames({
    [styles.row]: true,
    [styles.rowLandingPad]: isLandingPadActive,
    [styles.rowCancelPad]: isLandingPadActive && !canDrop,
    [styles.rowSearchMatch]: isSearchMatch,
    [styles.rowSearchFocus]: isSearchFocus,
    [className]: className,
  });

  const buttonClassname = classNames({
    [styles.button]: true,
    [styles.buttonExpanded]: node.expanded,
    [styles.buttonCollapsed]: !node.expanded,
  });

  const nodeContent = (
    <div style={{ height: "100%" }} {...otherProps}>
      {toggleChildrenVisibility &&
        node.children &&
        node.children.length > 0 && (
          <IconButton
            type="button"
            variant="ghost"
            color="gray"
            size="1"
            aria-label={node.expanded ? "Collapse" : "Expand"}
            aria-expanded={!!node.expanded}
            className={buttonClassname}
            style={{
              left: (lowerSiblingCounts.length - 0.5) * scaffoldBlockPxWidth,
            }}
            onClick={() =>
              toggleChildrenVisibility({
                node,
                path,
                treeIndex,
              })
            }
          >
            <FontAwesome
              ariaHidden={true}
              name={"fa-chevron-right"}
              prefix={"fas"}
            />
          </IconButton>
        )}

      <div
        className={
          styles.rowWrapper +
          (!canDrag ? ` ${styles.rowWrapperDragDisabled}` : "")
        }
      >
        {/* Set the row preview to be used during drag and drop */}
        {connectDragPreview(
          <div style={{ display: "flex", height: "100%" }}>
            {scaffold}
            <div
              className={rowClassName}
              style={{
                opacity: isDraggedDescendant ? 0.5 : 1,
                ...style,
              }}
            >
              <div
                className={
                  styles.rowContents +
                  (!canDrag ? ` ${styles.rowContentsDragDisabled}` : "")
                }
              >
                {handle}
                <div className={styles.rowToolbar}>
                  {icons.map((icon, index) => (
                    <div key={index} className={styles.toolbarButton}>
                      {icon}
                    </div>
                  ))}
                </div>

                <div className={styles.rowLabel}>
                  <span className={styles.rowTitle}>
                    {typeof nodeTitle === "function"
                      ? nodeTitle({
                          node,
                          path,
                          treeIndex,
                        })
                      : nodeTitle}
                  </span>
                </div>

                <div className={styles.rowToolbar}>{buttons}</div>
              </div>
            </div>
          </div>,
        )}
      </div>
    </div>
  );

  return canDrag
    ? connectDragSource(nodeContent, { dropEffect: "copy" })
    : nodeContent;
};

export default FileThemeNodeContentRenderer;
